import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';

const steps = ['Select sector', 'Select pilot (on behalf of)', 'Review & submit'];

const CAMO_BACKFILL_ROLES = [ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CA_MANAGER];

const formatPilotNames = (assignedPilots = []) => {
  if (!Array.isArray(assignedPilots) || assignedPilots.length === 0) return '—';
  const names = assignedPilots
    .map((p) => `${p?.firstName || ''} ${p?.lastName || ''}`.trim())
    .filter(Boolean);
  return names.length > 0 ? names.join(', ') : '—';
};

const formatCharterDate = (sector) => {
  const rawDate = sector?.departure?.date;
  if (!rawDate) return '—';
  const parsed = dayjs(rawDate);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : '—';
};

const TechnicalLogBookBackfill = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState('from_sector'); // 'from_sector' | 'standalone'
  const [activeStep, setActiveStep] = useState(0);
  const [sectors, setSectors] = useState([]);
  const [loadingSectors, setLoadingSectors] = useState(true);
  const [aircraft, setAircraft] = useState([]);
  const [loadingAircraft, setLoadingAircraft] = useState(false);
  const [selectedAircraftId, setSelectedAircraftId] = useState('');
  const [selectedSector, setSelectedSector] = useState(null);
  const [charterSearch, setCharterSearch] = useState('');
  const [aircraftFilterId, setAircraftFilterId] = useState('');
  const [pilotId, setPilotId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingSectors(true);
      try {
        const { data } = await axios.get('/api/technical-log-book/sectors-eligible-for-backfill');
        if (!cancelled) setSectors(data);
      } catch (err) {
        if (!cancelled) {
          showToast(err.response?.data?.message || 'Failed to load sectors', 'error');
        }
      } finally {
        if (!cancelled) setLoadingSectors(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showToast]);

  useEffect(() => {
    if (mode !== 'standalone') return;
    let cancelled = false;
    const load = async () => {
      setLoadingAircraft(true);
      try {
        const { data } = await axios.get('/api/aircraft');
        if (!cancelled) setAircraft(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) showToast(err.response?.data?.message || 'Failed to load aircraft', 'error');
      } finally {
        if (!cancelled) setLoadingAircraft(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [mode, showToast]);

  useEffect(() => {
    setPilotId('');
  }, [selectedSector?._id]);

  useEffect(() => {
    if (mode === 'standalone') {
      setActiveStep(0);
      setSelectedAircraftId('');
    } else {
      setActiveStep(0);
      setSelectedSector(null);
      setPilotId('');
      setCharterSearch('');
      setAircraftFilterId('');
    }
  }, [mode]);

  const aircraftFilterOptions = useMemo(() => {
    const byId = new Map();
    for (const s of sectors) {
      const id = s.aircraftId?._id || s.aircraftId;
      if (!id) continue;
      const key = String(id);
      if (byId.has(key)) continue;
      byId.set(key, {
        _id: key,
        registration: s.aircraftId?.registration || key,
        model: s.aircraftId?.model || ''
      });
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a.registration).localeCompare(String(b.registration))
    );
  }, [sectors]);

  const filteredSectors = useMemo(() => {
    const q = charterSearch.trim().toLowerCase();
    return sectors.filter((s) => {
      if (aircraftFilterId) {
        const sectorAircraftId = String(s.aircraftId?._id || s.aircraftId || '');
        if (sectorAircraftId !== String(aircraftFilterId)) return false;
      }
      if (!q) return true;
      const charterNumber = String(s.tripId?.charterNumber || '').toLowerCase();
      const title = String(s.tripId?.title || '').toLowerCase();
      return charterNumber.includes(q) || title.includes(q);
    });
  }, [sectors, charterSearch, aircraftFilterId]);

  useEffect(() => {
    if (!selectedSector) return;
    const stillVisible = filteredSectors.some((s) => s._id === selectedSector._id);
    if (!stillVisible) setSelectedSector(null);
  }, [filteredSectors, selectedSector]);

  const pilots = selectedSector?.assignedPilots || [];

  if (user && !CAMO_BACKFILL_ROLES.includes(user.role)) {
    return <Navigate to="/technical-log-book" replace />;
  }

  const handleNext = () => {
    if (activeStep === 0 && !selectedSector) {
      showToast('Please select a sector', 'warning');
      return;
    }
    if (activeStep === 1 && !pilotId) {
      showToast('Please select the pilot (on behalf of)', 'warning');
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `/api/crew/sectors/${selectedSector._id}/flight-log/backfill`,
        { pilotId }
      );
      showToast(data.message || 'Technical Log Book entry created. Complete CAMO fields.', 'success');
      const tlbId = data.technicalLogBook?._id;
      if (tlbId) {
        navigate(`/technical-log-book`, { state: { openEntryId: tlbId } });
      } else {
        navigate('/technical-log-book');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Backfill failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitStandalone = async () => {
    if (!selectedAircraftId) {
      showToast('Please select an aircraft', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post('/api/technical-log-book/standalone', {
        aircraftId: selectedAircraftId
      });
      showToast('Technical Log Book entry created. Complete CAMO fields.', 'success');
      const tlbId = data?._id;
      if (tlbId) {
        navigate(`/technical-log-book`, { state: { openEntryId: tlbId } });
      } else {
        navigate('/technical-log-book');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create entry', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center" gap={1}>
        <Button component={RouterLink} to="/technical-log-book" size="small" sx={{ textTransform: 'none' }}>
          Technical Log Book
        </Button>
        <Typography color="text.secondary">/</Typography>
        <Typography variant="subtitle1" color="text.primary">
          Create entry
        </Typography>
      </Box>
      <Typography variant="h5" gutterBottom>
        Create Technical Log Book entry
      </Typography>

      <Tabs value={mode} onChange={(_, v) => setMode(v)} sx={{ mb: 2 }}>
        <Tab value="from_sector" label="From past sector (charter / pilot)" />
        <Tab value="standalone" label="Standalone (no sector — e.g. existing FDTL)" />
      </Tabs>

      {mode === 'standalone' && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Create a Technical Log Book entry without linking to a charter, sector, or crew. Use this to add existing FDTL records. You only need to select the aircraft; then complete the CAMO fields in the entry form.
          </Alert>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select aircraft
            </Typography>
            {loadingAircraft ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="standalone-aircraft-label">Aircraft</InputLabel>
                <Select
                  labelId="standalone-aircraft-label"
                  label="Aircraft"
                  value={selectedAircraftId}
                  onChange={(e) => setSelectedAircraftId(e.target.value)}
                >
                  <MenuItem value="">Select aircraft…</MenuItem>
                  {aircraft.map((a) => (
                    <MenuItem key={a._id} value={a._id}>
                      {a.registration || a.serialNumber || a._id} {a.model ? `(${a.model})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Paper>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSubmitStandalone}
              disabled={submitting || !selectedAircraftId || loadingAircraft}
            >
              {submitting ? 'Creating…' : 'Create entry'}
            </Button>
          </Box>
        </>
      )}

      {mode === 'from_sector' && (
        <>
      <Alert severity="info" sx={{ mb: 3 }}>
        Use this flow to create a Technical Log Book entry for a past sector when no pilot log was uploaded. You act on behalf of the pilot; the pilot you select will be recorded as the creator of the flight log entry.
      </Alert>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Select sector */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sectors without a POST_FLIGHT Flight Log (eligible for backfill)
          </Typography>
          {loadingSectors ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : sectors.length === 0 ? (
            <Alert severity="info">No sectors eligible for backfill. Only COMPLETED or SECTOR_COMPLETED sectors without an existing post-flight entry are listed.</Alert>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 2,
                  alignItems: 'flex-start'
                }}
              >
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="sector-aircraft-filter-label">Aircraft</InputLabel>
                  <Select
                    labelId="sector-aircraft-filter-label"
                    label="Aircraft"
                    value={aircraftFilterId}
                    onChange={(e) => setAircraftFilterId(e.target.value)}
                  >
                    <MenuItem value="">All Aircraft</MenuItem>
                    {aircraftFilterOptions.map((ac) => (
                      <MenuItem key={ac._id} value={ac._id}>
                        {ac.registration}{ac.model ? ` — ${ac.model}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search charter number or title…"
                  value={charterSearch}
                  onChange={(e) => setCharterSearch(e.target.value)}
                  sx={{ flex: 1, minWidth: 220, maxWidth: 420 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: charterSearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="clear charter search"
                          onClick={() => setCharterSearch('')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </Box>
              {filteredSectors.length === 0 ? (
                <Alert severity="info">
                  {aircraftFilterId || charterSearch.trim()
                    ? `No sectors match the current filters${charterSearch.trim() ? ` (“${charterSearch.trim()}”)` : ''}. Try a different aircraft or charter search.`
                    : 'No sectors eligible for backfill.'}
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Select</TableCell>
                        <TableCell>Charter / Trip</TableCell>
                        <TableCell>Charter Date</TableCell>
                        <TableCell>Sector</TableCell>
                        <TableCell>Pilot Name</TableCell>
                        <TableCell>Aircraft</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSectors.map((s) => (
                        <TableRow
                          key={s._id}
                          selected={selectedSector?._id === s._id}
                          onClick={() => setSelectedSector(s)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Radio checked={selectedSector?._id === s._id} />
                          </TableCell>
                          <TableCell>{s.tripId?.charterNumber || s.tripId?.title || '—'}</TableCell>
                          <TableCell>{formatCharterDate(s)}</TableCell>
                          <TableCell>{s.seqNo != null ? `Sector ${s.seqNo}` : '—'}</TableCell>
                          <TableCell>{formatPilotNames(s.assignedPilots)}</TableCell>
                          <TableCell>{s.aircraftId?.registration || '—'}</TableCell>
                          <TableCell>{s.status || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Step 1: Select pilot */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Pilot (on behalf of)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            The selected pilot will be recorded as the creator of the flight log entry. Choose from the pilots assigned to this sector.
          </Typography>
          {pilots.length === 0 ? (
            <Alert severity="warning">No pilots assigned to this sector. Assign pilots in the charter/sector before backfilling.</Alert>
          ) : (
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Assigned pilots</FormLabel>
              <RadioGroup value={pilotId} onChange={(e) => setPilotId(e.target.value)}>
                {pilots.map((p) => (
                  <FormControlLabel
                    key={p._id}
                    value={p._id}
                    control={<Radio />}
                    label={`${p.firstName || ''} ${p.lastName || ''} ${p.email ? `(${p.email})` : ''}`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </Paper>
      )}

      {/* Step 2: Review */}
      {activeStep === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Review
          </Typography>
          <Box component="dl" sx={{ m: 0 }}>
            <Typography component="dt" variant="caption" color="text.secondary">Sector</Typography>
            <Typography component="dd" sx={{ mb: 1 }}>{selectedSector?.tripId?.charterNumber || selectedSector?.tripId?.title} — Sector {selectedSector?.seqNo} ({selectedSector?.aircraftId?.registration})</Typography>
            <Typography component="dt" variant="caption" color="text.secondary">On behalf of pilot</Typography>
            <Typography component="dd" sx={{ mb: 1 }}>
              {pilots.find((p) => p._id === pilotId)
                ? `${pilots.find((p) => p._id === pilotId).firstName} ${pilots.find((p) => p._id === pilotId).lastName}`
                : pilotId}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Submitting will create a Flight Log Entry (attributed to the selected pilot) and a Technical Log Book entry. Sector status will not be changed. Complete flight and CAMO fields in the Technical Log Book entry form.
          </Alert>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        <Box>
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          )}
        </Box>
      </Box>
        </>
      )}
    </Box>
  );
};

export default TechnicalLogBookBackfill;
